import { gql } from '@apollo/client';

// Appointment mutations
export const CREATE_APPOINTMENT = gql`
  mutation CreateAppointment(
    $storeId: ID!
    $staffId: ID!
    $serviceId: ID!
    $scheduledDate: DateTime!
    $clientName: String!
    $clientPhone: String!
    $clientEmail: String!
    $clientComment: String
  ) {
    createAppointment(
      storeId: $storeId
      staffId: $staffId
      serviceId: $serviceId
      scheduledDate: $scheduledDate
      clientName: $clientName
      clientPhone: $clientPhone
      clientEmail: $clientEmail
      clientComment: $clientComment
    ) {
      ok
      errors
      appointment {
        id
        scheduledDate
        staff {
          id
          fullName
        }
        service {
          id
          name
        }
        client {
          id
          name
          email
        }
      }
    }
  }
`;

export const UPDATE_APPOINTMENT = gql`
  mutation UpdateAppointment(
    $appointmentId: ID!
    $storeId: ID
    $staffId: ID
    $serviceId: ID
    $scheduledDate: DateTime
    $clientName: String
    $clientPhone: String
    $clientEmail: String
    $clientComment: String
    $status: String
  ) {
    updateAppointment(
      appointmentId: $appointmentId
      storeId: $storeId
      staffId: $staffId
      serviceId: $serviceId
      scheduledDate: $scheduledDate
      clientName: $clientName
      clientPhone: $clientPhone
      clientEmail: $clientEmail
      clientComment: $clientComment
      status: $status
    ) {
      ok
      errors
      appointment {
        id
        scheduledDate
        bookingGroupId
        staff {
          id
          fullName
        }
        service {
          id
          name
        }
        client {
          id
          name
          email
        }
      }
    }
  }
`;

export const DELETE_APPOINTMENT = gql`
  mutation DeleteAppointment($appointmentId: ID!) {
    deleteAppointment(appointmentId: $appointmentId) {
      ok
      errors
    }
  }
`;

export const ISSUE_REFUND = gql`
  mutation IssueRefund($appointmentId: ID!) {
    issueRefund(appointmentId: $appointmentId) {
      ok
      refundId
      errors
    }
  }
`;

export const SYNC_PAYMENT_STATUS = gql`
  mutation SyncPaymentStatus($appointmentId: ID!) {
    syncAppointmentPaymentStatus(appointmentId: $appointmentId) {
      ok
      paymentStatus
      errors
    }
  }
`;

// Staff mutations
export const CREATE_STAFF = gql`
  mutation CreateStaff(
    $firstName: String!
    $lastName: String!
    $username: String!
    $email: String!
    $password: String
    $sendInviteEmail: Boolean
    $storeIds: [UUID]!
    $role: String!
    $title: String
    $services: [ID]
    $isActive: Boolean
  ) {
    createStaff(
      firstName: $firstName
      lastName: $lastName
      username: $username
      email: $email
      password: $password
      sendInviteEmail: $sendInviteEmail
      storeIds: $storeIds
      role: $role
      title: $title
      services: $services
      isActive: $isActive
    ) {
      ok
      errors
      staff {
        id
        firstName
        lastName
        username
        email
        fullName
        services {
          id
          name
          duration
          price
        }
        staffStores {
          id
          role
          title
          isActive
          store {
            id
            name
          }
        }
      }
    }
  }
`;

export const UPDATE_STAFF = gql`
  mutation UpdateStaff(
    $staffId: ID!
    $firstName: String
    $lastName: String
    $username: String
    $email: String
    $services: [ID]
    $role: String
    $title: String
    $storeIds: [UUID]
    $isActive: Boolean
  ) {
    updateStaff(
      staffId: $staffId
      firstName: $firstName
      lastName: $lastName
      username: $username
      email: $email
      services: $services
      role: $role
      title: $title
      storeIds: $storeIds
      isActive: $isActive
    ) {
      ok
      errors
      staff {
        id
        firstName
        lastName
        username
        email
        fullName
        services {
          id
          name
          duration
          price
        }
        staffStores {
          id
          role
          title
          isActive
          store {
            id
            name
          }
        }
      }
    }
  }
`;

export const DELETE_STAFF = gql`
  mutation DeleteStaff($staffId: ID!) {
    deleteStaff(staffId: $staffId) {
      ok
      errors
    }
  }
`;

// Service mutations
export const CREATE_SERVICE = gql`
  mutation CreateService(
    $storeId: ID!
    $name: String!
    $duration: Int!
    $price: Decimal!
    $isActive: Boolean
  ) {
    createService(
      storeId: $storeId
      name: $name
      duration: $duration
      price: $price
      isActive: $isActive
    ) {
      ok
      errors
      service {
        id
        name
        duration
        price
        isActive
      }
    }
  }
`;

export const UPDATE_SERVICE = gql`
  mutation UpdateService(
    $serviceId: ID!
    $name: String
    $duration: Int
    $price: Decimal
    $isActive: Boolean
  ) {
    updateService(
      serviceId: $serviceId
      name: $name
      duration: $duration
      price: $price
      isActive: $isActive
    ) {
      ok
      errors
      service {
        id
        name
        duration
        price
        isActive
      }
    }
  }
`;

export const DELETE_SERVICE = gql`
  mutation DeleteService($serviceId: ID!) {
    deleteService(serviceId: $serviceId) {
      ok
      errors
    }
  }
`;

// Store mutations
export const CREATE_STORE = gql`
  mutation CreateStore(
    $networkId: ID!
    $name: String!
    $description: String
    $address: String
    $phone: String
    $email: String
    $weekdayStartTime: Time
    $weekdayEndTime: Time
    $weekendStartTime: Time
    $weekendEndTime: Time
    $isActive: Boolean
  ) {
    createStore(
      networkId: $networkId
      name: $name
      description: $description
      address: $address
      phone: $phone
      email: $email
      weekdayStartTime: $weekdayStartTime
      weekdayEndTime: $weekdayEndTime
      weekendStartTime: $weekendStartTime
      weekendEndTime: $weekendEndTime
      isActive: $isActive
    ) {
      ok
      errors
      store {
        id
        name
        description
        address
        phone
        email
        weekdayStartTime
        weekdayEndTime
        weekendStartTime
        weekendEndTime
        isActive
      }
    }
  }
`;

export const UPDATE_STORE = gql`
  mutation UpdateStore(
    $storeId: ID!
    $name: String
    $description: String
    $address: String
    $phone: String
    $email: String
    $weekdayStartTime: Time
    $weekdayEndTime: Time
    $weekendStartTime: Time
    $weekendEndTime: Time
    $fileName: String
    $fileData: String
    $carouselImages: [ImagesType]
    $removeAvatar: Boolean
    $removeCarouselImages: [String]
    $isActive: Boolean
    $paymentRequired: Boolean
    $instagramUrl: String
    $facebookUrl: String
    $whatsappUrl: String
    $telegramUrl: String
    $linkedinUrl: String
  ) {
    updateStore(
      storeId: $storeId
      name: $name
      description: $description
      address: $address
      phone: $phone
      email: $email
      weekdayStartTime: $weekdayStartTime
      weekdayEndTime: $weekdayEndTime
      weekendStartTime: $weekendStartTime
      weekendEndTime: $weekendEndTime
      fileName: $fileName
      fileData: $fileData
      carouselImages: $carouselImages
      removeAvatar: $removeAvatar
      removeCarouselImages: $removeCarouselImages
      isActive: $isActive
      paymentRequired: $paymentRequired
      instagramUrl: $instagramUrl
      facebookUrl: $facebookUrl
      whatsappUrl: $whatsappUrl
      telegramUrl: $telegramUrl
      linkedinUrl: $linkedinUrl
    ) {
      ok
      errors
      store {
        id
        name
        description
        address
        phone
        email
        weekdayStartTime
        weekdayEndTime
        weekendStartTime
        weekendEndTime
        isActive
        paymentRequired
        chargesEnabled
        instagramUrl
        facebookUrl
        whatsappUrl
        telegramUrl
        linkedinUrl
        avatar
        images {
          id
          image
          imageType
        }
      }
    }
  }
`;

export const DELETE_STORE = gql`
  mutation DeleteStore($storeId: ID!) {
    deleteStore(storeId: $storeId) {
      ok
      errors
    }
  }
`;

// Network mutations
export const CREATE_NETWORK = gql`
  mutation CreateNetwork($name: String!) {
    createNetwork(name: $name) {
      ok
      errors
      network {
        id
        name
      }
    }
  }
`;

export const UPDATE_NETWORK = gql`
  mutation UpdateNetwork($networkId: ID!, $name: String!) {
    updateNetwork(networkId: $networkId, name: $name) {
      ok
      errors
      network {
        id
        name
      }
    }
  }
`;

export const DELETE_NETWORK = gql`
  mutation DeleteNetwork($networkId: ID!) {
    deleteNetwork(networkId: $networkId) {
      ok
      errors
    }
  }
`;

// Client mutations
export const CREATE_CLIENT = gql`
  mutation CreateClient(
    $name: String!
    $phone: String
    $email: String
    $comment: String
    $needToReach: Boolean
  ) {
    createClient(
      name: $name
      phone: $phone
      email: $email
      comment: $comment
      needToReach: $needToReach
    ) {
      ok
      errors
      client {
        id
        name
        phone
        email
        comment
        needToReach
      }
    }
  }
`;

// Rating mutations
export const CREATE_RATING = gql`
  mutation CreateRating(
    $rating: Decimal!
    $comment: String
    $clientName: String
    $clientPhone: String
    $clientEmail: String
    $staffId: ID
    $storeId: ID
    $networkId: ID
  ) {
    createRating(
      rating: $rating
      comment: $comment
      clientName: $clientName
      clientPhone: $clientPhone
      clientEmail: $clientEmail
      staffId: $staffId
      storeId: $storeId
      networkId: $networkId
    ) {
      ok
      errors
      rating {
        id
        rating
        comment
        clientName
        clientPhone
        clientEmail
      }
    }
  }
`;

export const UPDATE_RATING = gql`
  mutation UpdateRating(
    $ratingId: ID!
    $rating: Decimal
    $comment: String
    $clientName: String
    $clientPhone: String
    $clientEmail: String
  ) {
    updateRating(
      ratingId: $ratingId
      rating: $rating
      comment: $comment
      clientName: $clientName
      clientPhone: $clientPhone
      clientEmail: $clientEmail
    ) {
      ok
      errors
      rating {
        id
        rating
        comment
        clientName
        clientPhone
        clientEmail
      }
    }
  }
`;

export const DELETE_RATING = gql`
  mutation DeleteRating($ratingId: ID!) {
    deleteRating(ratingId: $ratingId) {
      ok
      errors
    }
  }
`;

// Payment / Stripe mutations
export const CREATE_PLATFORM_SUBSCRIPTION = gql`
  mutation CreatePlatformSubscription($storeId: ID!) {
    createPlatformSubscription(storeId: $storeId) {
      ok
      subscriptionId
      errors
    }
  }
`;

export const CREATE_STRIPE_ACCOUNT = gql`
  mutation CreateStripeAccount($storeId: ID!, $refreshUrl: String, $returnUrl: String) {
    createStripeAccount(storeId: $storeId, refreshUrl: $refreshUrl, returnUrl: $returnUrl) {
      ok
      errors
      result {
        accountId
        onboardingUrl
      }
    }
  }
`;

// Staff Service mutations
export const CREATE_STAFF_SERVICE = gql`
  mutation CreateStaffService($staffId: ID!, $serviceId: ID!) {
    createStaffService(staffId: $staffId, serviceId: $serviceId) {
      ok
      errors
    }
  }
`;

export const DELETE_STAFF_SERVICE = gql`
  mutation DeleteStaffService($staffId: ID!, $serviceId: ID!) {
    deleteStaffService(staffId: $staffId, serviceId: $serviceId) {
      ok
      errors
    }
  }
`;

// Schedule mutations
export const CREATE_SCHEDULE = gql`
  mutation CreateSchedule($storeId: ID!, $date: Date!) {
    createSchedule(storeId: $storeId, date: $date) {
      ok
      errors
      schedule {
        id
        date
      }
    }
  }
`;

export const CREATE_WEEKLY_SCHEDULE = gql`
  mutation CreateWeeklySchedule(
    $staffId: ID!
    $storeId: ID!
    $scheduledDate: Date!
    $times: [ScheduleTimeType!]!
    $recurring: Int
  ) {
    createWeeklySchedule(
      staffId: $staffId
      storeId: $storeId
      scheduledDate: $scheduledDate
      times: $times
      recurring: $recurring
    ) {
      ok
      errors
    }
  }
`;

export const DELETE_SCHEDULE = gql`
  mutation DeleteSchedule($scheduleId: ID!) {
    deleteSchedule(scheduleId: $scheduleId) {
      ok
      errors
    }
  }
`;

// Staff Schedule mutations
export const CREATE_STAFF_SCHEDULE = gql`
  mutation CreateStaffSchedule(
    $staffId: ID!
    $scheduleId: ID!
    $startTime: Time!
    $endTime: Time!
    $startBreak: Time
    $endBreak: Time
  ) {
    createStaffSchedule(
      staffId: $staffId
      scheduleId: $scheduleId
      startTime: $startTime
      endTime: $endTime
      startBreak: $startBreak
      endBreak: $endBreak
    ) {
      ok
      errors
    }
  }
`;

export const UPDATE_STAFF_SCHEDULE = gql`
  mutation UpdateStaffSchedule(
    $staffScheduleId: ID!
    $startTime: Time
    $endTime: Time
    $startBreak: Time
    $endBreak: Time
  ) {
    updateStaffSchedule(
      staffScheduleId: $staffScheduleId
      startTime: $startTime
      endTime: $endTime
      startBreak: $startBreak
      endBreak: $endBreak
    ) {
      ok
      errors
    }
  }
`;

export const DELETE_STAFF_SCHEDULE = gql`
  mutation DeleteStaffSchedule($staffScheduleId: ID!) {
    deleteStaffSchedule(staffScheduleId: $staffScheduleId) {
      ok
      errors
    }
  }
`;

// Platform subscription — two-step signup flow.
// Step 1: get a clientSecret so Stripe Elements can collect card details.
// intentType is 'payment' (monthly) or 'setup' (annual) — determines which
// Stripe.js method the frontend calls to confirm the intent.
export const INIT_PLATFORM_BILLING = gql`
  mutation InitPlatformBilling($storeId: ID!, $billingInterval: String!) {
    initPlatformBilling(storeId: $storeId, billingInterval: $billingInterval) {
      ok
      clientSecret
      intentType
      errors
    }
  }
`;

// Step 2: after Stripe.js confirmation succeeds on the client, create the
// recurring subscription server-side. paymentIntentId is required for monthly.
export const ACTIVATE_PLATFORM_SUBSCRIPTION = gql`
  mutation ActivatePlatformSubscription(
    $storeId: ID!
    $billingInterval: String!
    $paymentMethodId: String!
    $paymentIntentId: String
  ) {
    activatePlatformSubscription(
      storeId: $storeId
      billingInterval: $billingInterval
      paymentMethodId: $paymentMethodId
      paymentIntentId: $paymentIntentId
    ) {
      ok
      errors
      store {
        id
        platformSubscriptionStatus
        platformBillingInterval
        platformSubscriptionId
      }
    }
  }
`;

// Platform subscription management — cancel or switch billing interval.
export const CANCEL_PLATFORM_SUBSCRIPTION = gql`
  mutation CancelPlatformSubscription($storeId: ID!, $cancelAtPeriodEnd: Boolean) {
    cancelPlatformSubscription(storeId: $storeId, cancelAtPeriodEnd: $cancelAtPeriodEnd) {
      ok
      errors
      store {
        id
        platformSubscriptionStatus
        platformSubscriptionCancelAtPeriodEnd
      }
    }
  }
`;

export const SWITCH_PLATFORM_BILLING_INTERVAL = gql`
  mutation SwitchPlatformBillingInterval($storeId: ID!, $billingInterval: String!) {
    switchPlatformBillingInterval(storeId: $storeId, billingInterval: $billingInterval) {
      ok
      errors
      store {
        id
        platformBillingInterval
        platformSubscriptionCancelAtPeriodEnd
      }
    }
  }
`;

// Per-seat licensing — owner buys/releases seats. Backend updates the
// Stripe subscription quantity (Stripe pro-rates the next invoice).
// paymentMethodId is only required for the very first paid purchase (when
// there is no existing seat subscription). Use CREATE_SEAT_BILLING_SETUP_INTENT
// to collect the card beforehand.
export const UPDATE_STORE_SEAT_COUNT = gql`
  mutation UpdateStoreSeatCount($storeId: ID!, $newSeatCount: Int!, $paymentMethodId: String) {
    updateStoreSeatCount(storeId: $storeId, newSeatCount: $newSeatCount, paymentMethodId: $paymentMethodId) {
      ok
      errors
      store {
        id
        seatCount
        activeSeatsUsed
        seatLicenseSubscriptionId
      }
    }
  }
`;

// Creates a Stripe SetupIntent (and platform Customer if needed) so the
// owner can save a card for seat-licence billing. Confirm the returned
// clientSecret with Stripe Elements, then pass the payment_method ID to
// UPDATE_STORE_SEAT_COUNT.
export const CREATE_SEAT_BILLING_SETUP_INTENT = gql`
  mutation CreateSeatBillingSetupIntent($storeId: ID!) {
    createSeatBillingSetupIntent(storeId: $storeId) {
      ok
      clientSecret
      errors
    }
  }
`;

// ─── Barber public profile (self-edit + admin override) ─────────────────────

// Edits the slug / bio / title fields on Staff. Backend returns
// errors: ["slug_taken"] specifically on slug collision so the frontend can
// show a targeted validation message.
export const UPDATE_STAFF_PROFILE = gql`
  mutation UpdateStaffProfile(
    $staffId: ID!
    $bio: String
    $title: String
    $slug: String
    $fileData: String
    $fileName: String
    $instagramUrl: String
    $facebookUrl: String
    $whatsappUrl: String
    $telegramUrl: String
    $linkedinUrl: String
    $tiktokUrl: String
  ) {
    updateStaffProfile(
      staffId: $staffId
      bio: $bio
      title: $title
      slug: $slug
      fileData: $fileData
      fileName: $fileName
      instagramUrl: $instagramUrl
      facebookUrl: $facebookUrl
      whatsappUrl: $whatsappUrl
      telegramUrl: $telegramUrl
      linkedinUrl: $linkedinUrl
      tiktokUrl: $tiktokUrl
    ) {
      ok
      errors
      staff {
        id
        slug
        bio
        title
        avatar
        instagramUrl
        facebookUrl
        whatsappUrl
        telegramUrl
        linkedinUrl
        tiktokUrl
      }
    }
  }
`;

// Append portfolio images. Additive — doesn't wipe existing ones. Each file
// is base64-encoded and paired with its original filename.
export const UPLOAD_STAFF_PORTFOLIO_IMAGES = gql`
  mutation UploadStaffPortfolioImages($staffId: ID!, $images: [PortfolioImageInput!]!) {
    uploadStaffPortfolioImages(staffId: $staffId, images: $images) {
      ok
      errors
      staff {
        id
        portfolio {
          id
          url
        }
      }
    }
  }
`;

// Remove a single portfolio image. Takes the image_id (from the portfolio
// array), not the staff_id — backend resolves the staff via the Image FK
// and runs the same self-or-admin permission check.
export const DELETE_STAFF_PORTFOLIO_IMAGE = gql`
  mutation DeleteStaffPortfolioImage($imageId: ID!) {
    deleteStaffPortfolioImage(imageId: $imageId) {
      ok
      errors
    }
  }
`;
